/**
 * @spec docs/specs/features/staking-position-ui.md
 * React Router setup and route configuration
 *
 * Uses React.lazy() for route-based code splitting.
 * Each page loads only when navigated to, reducing initial bundle size.
 */
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { routes } from './routes';
import { Spinner } from '@/components/ui/Spinner';
import { Layout } from '@/components/layout/Layout';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Lazy-loaded page components (route-based code splitting)
const HomePage = lazy(() => import('@/pages/HomePage').then((m) => ({ default: m.HomePage })));
const AddressPage = lazy(() =>
  import('@/pages/AddressPage').then((m) => ({ default: m.AddressPage }))
);
const StakePage = lazy(() => import('@/pages/StakePage').then((m) => ({ default: m.StakePage })));
const ClaimPage = lazy(() => import('@/pages/ClaimPage').then((m) => ({ default: m.ClaimPage })));
const SwapPage = lazy(() => import('@/pages/SwapPage').then((m) => ({ default: m.SwapPage })));

/**
 * Loading fallback for lazy-loaded pages
 */
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="container mx-auto px-4 py-8 text-center">
      <h1 className="text-6xl font-heading font-bold text-accent-purple">404</h1>
      <p className="mt-4 text-text-secondary">Page not found</p>
      <a
        href={routes.home()}
        className="mt-8 inline-block text-accent-purple hover:text-accent-purple-light"
      >
        Go back home
      </a>
    </div>
  );
}

/**
 * App routes configuration
 *
 * All pages are lazy-loaded for better initial load performance.
 * Layout provides consistent header across all pages.
 */
export function AppRoutes() {
  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route
            path={routes.home()}
            element={
              <ErrorBoundary>
                <HomePage />
              </ErrorBoundary>
            }
          />
          <Route
            path={routes.address(':address')}
            element={
              <ErrorBoundary>
                <AddressPage />
              </ErrorBoundary>
            }
          />
          {/* Redirect old /stats route to dashboard for backwards compatibility */}
          <Route path="/stats" element={<Navigate to={routes.home()} replace />} />
          <Route
            path={routes.stake()}
            element={
              <ErrorBoundary>
                <StakePage />
              </ErrorBoundary>
            }
          />
          <Route
            path={routes.claim()}
            element={
              <ErrorBoundary>
                <ClaimPage />
              </ErrorBoundary>
            }
          />
          <Route
            path={routes.swap()}
            element={
              <ErrorBoundary>
                <SwapPage />
              </ErrorBoundary>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}
